import { useCallback } from "react";

import { showServiceStart, showServiceStop } from "@/components/environments/service-actions";
import { useToast } from "@/components/flexkit/toast";
import { useAutoStartEnvironment } from "@/hooks/use-auto-start-environment";
import { useStartService, useStopService, type PlainService } from "@/queries/automations-queries";
import { formatError } from "@/utils/errors";

type ServiceToggleAction = "start" | "stop" | "none";

export function serviceToggleAction(service: PlainService): ServiceToggleAction {
    if (showServiceStart(service)) return "start";
    if (showServiceStop(service)) return "stop";
    return "none";
}

export function useToggleService(environmentId: string) {
    const startService = useStartService();
    const stopService = useStopService();
    const { ensureEnvironmentStarted } = useAutoStartEnvironment(environmentId);
    const { toast } = useToast();

    const toggle = useCallback(
        async (service: PlainService, shouldStart: boolean) => {
            const name = service.metadata?.name || "service";
            try {
                if (shouldStart) {
                    await ensureEnvironmentStarted();
                    await startService.mutateAsync({ serviceId: service.id, environmentId });
                } else {
                    await stopService.mutateAsync({ serviceId: service.id, environmentId });
                }
            } catch (error) {
                toast({
                    title: `Failed to ${shouldStart ? "start" : "stop"} ${name}`,
                    description: formatError(error),
                });
            }
        },
        [startService, stopService, ensureEnvironmentStarted, environmentId, toast],
    );

    return toggle;
}
